// Typed fetch helpers for the solo gameplay endpoints. Mirrors the
// 8 routes registered by packages/server/src/routes/solo.ts. All
// requests carry the cookie (`credentials: 'include'`) so the
// requirePlayer preHandler resolves the active session.

import type {
  BuyRequest,
  CombineRequest,
  LockRequest,
  ReorderRequest,
  SellRequest,
  SoloBattleResponse,
  SoloStateResponse,
} from '@origami/shared'
import { type ApiResult } from './playerApi'

async function asResult<T>(res: Response): Promise<ApiResult<T>> {
  if (res.ok) return { ok: true, value: (await res.json()) as T }
  let code = 'unknown'
  let message: string | undefined
  try {
    const body = (await res.json()) as { error?: { code?: string; message?: string } }
    code = body?.error?.code ?? code
    message = body?.error?.message
  } catch {
    // body not JSON
  }
  return { ok: false, code, message, status: res.status }
}

/** Variant of asResult for endpoints that return 204 No Content on
 *  success. Reading `.json()` on an empty body throws — branch on
 *  status first. */
async function asVoidResult(res: Response): Promise<ApiResult<void>> {
  if (res.ok) return { ok: true, value: undefined }
  let code = 'unknown'
  let message: string | undefined
  try {
    const body = (await res.json()) as { error?: { code?: string; message?: string } }
    code = body?.error?.code ?? code
    message = body?.error?.message
  } catch {
    // body not JSON
  }
  return { ok: false, code, message, status: res.status }
}

async function post<T>(url: string, body: object = {}): Promise<ApiResult<T>> {
  const res = await fetch(url, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })
  return asResult(res)
}

// =============================================================
//  Shop-phase endpoints — all return { state: SoloSessionState }
// =============================================================

export const startSolo = () => post<SoloStateResponse>('/api/solo/start')

/** GET /api/solo/session — probe-only. 200 with current state, or
 *  ok:false with code 'match_not_found' / 'no_session' / etc. when no
 *  active run exists. Used by the title screen to decide between
 *  "Begin Solo Run" and "Continue Run + new run link". */
export const getSoloSession = async (): Promise<ApiResult<SoloStateResponse>> => {
  const res = await fetch('/api/solo/session', { credentials: 'include' })
  return asResult<SoloStateResponse>(res)
}

/** POST /api/solo/abandon — destructive. Marks the active match
 *  abandoned + participant eliminated so the next /start mints a
 *  fresh run. Returns 204 on success. */
export const abandonSoloSession = async (): Promise<ApiResult<void>> => {
  const res = await fetch('/api/solo/abandon', {
    method: 'POST',
    credentials: 'include',
  })
  return asVoidResult(res)
}

export const buyFromShop = (body: BuyRequest) =>
  post<SoloStateResponse>('/api/solo/buy', body)

export const sellUnit = (body: SellRequest) =>
  post<SoloStateResponse>('/api/solo/sell', body)

export const rerollShop = () => post<SoloStateResponse>('/api/solo/reroll')

export const toggleShopLock = (body: LockRequest) =>
  post<SoloStateResponse>('/api/solo/lock', body)

export const reorderUnits = (body: ReorderRequest) =>
  post<SoloStateResponse>('/api/solo/reorder', body)

export const combineUnits = (body: CombineRequest) =>
  post<SoloStateResponse>('/api/solo/combine', body)

// =============================================================
//  Battle resolution
// =============================================================

/** POST /api/solo/ready — resolves the round on the server, returns
 *  the BattleResult to play back, the new shop state for the next
 *  round, and meta (won/damage/ghost_round). */
export const readyForBattle = () =>
  post<SoloBattleResponse>('/api/solo/ready')
